import { useEffect, useState } from 'react';
import { Box, Drawer, IconButton, PaperProps, Tab, Tabs } from '@mui/material';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import { useNavigate, useLocation } from 'react-router';
import { t } from 'i18next';
import { EPageName } from '../data/Constants';

import type { TabDrawerProps } from '../../types/ITypes';

//This method returns the custom style for the drawer component
const getSXPaperProps = (isShowDrawer: boolean) => {
  return {
    position: isShowDrawer ? 'initial' : 'absolute',
    transition: '.15s',
    top: '105px',
    bottom: '0',
    height: '100%',
    minWidth: '150px',
    width: '150px',
  };
};

/**
 * This component renders the Tabs for a given set of tabs configuration.
 * @returns {JSX.Element} a  TabDrawer component.
 */
export const TabDrawer: React.FC<TabDrawerProps> = ({ tabsConfig }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isFeaturesPage = location.pathname.endsWith(EPageName.Feature);
  const [activeTab, setActiveTab] = useState(0);
  const [isShowDrawer, setIsShowDrawer] = useState(true);

  const { tabsData, icons = {} } = tabsConfig;

  useEffect(() => {
    if (isFeaturesPage) {
      setActiveTab(1);
    } else {
      setActiveTab(0);
    }
  }, [location.pathname]);

  //Set the active tab and navigate to the corresponding  page while changing the tab
  const onTabChange = (_: React.SyntheticEvent<Element, Event>, value: number) => {
    setActiveTab(value);
    navigate(`${tabsData[value].path}`);
  };

  //Show/hide the tab drawer while collapse and expand
  const onToggleDrawer = () => {
    setIsShowDrawer(!isShowDrawer);
  };

  const paperProps: Partial<PaperProps<'div', unknown>> = {
    sx: getSXPaperProps(isShowDrawer),
    className: `w-0 ${isShowDrawer ? 'drawerOpen' : ''} `,
  };

  let tabIndex = 0;
  let minitabIndex = 0;

  const getTabIcon = (index: number, isShowDrawer: boolean) => {
    const tab = tabsData[index];
    if (tab && icons[tab.id]) {
      // Use tab.id as the key
      return (
        <span data-testid={`icon-${tab.id}`}>
          {icons[tab.id]}
          {!isShowDrawer && <h3>{t(tab.name)?.charAt(0)?.toUpperCase()}</h3>}
        </span>
      );
    }
    return null;
  };

  const tabs = tabsData?.map((s) => (
    <Tab
      key={`${s.id}-${tabIndex}`}
      id={s.id}
      label={<span>{t(s.name)}</span>}
      value={tabIndex++}
      className="tab-drawer-tabs"
      iconPosition="start"
      icon={getTabIcon(tabIndex - 1, isShowDrawer)}
    />
  ));

  const minitabs = tabsData?.map((s) => (
    <Tab
      key={`${s.id}-${minitabIndex}`}
      id={s.id}
      value={minitabIndex++}
      className="tab-drawer-tabs"
      icon={getTabIcon(minitabIndex - 1, isShowDrawer)}
    />
  ));

  return (
    <>
      {isShowDrawer ? (
        <Drawer
          id="tab-drawer"
          variant="permanent"
          open
          className="h-100"
          slotProps={{
            paper: paperProps,
          }}
        >
          <Box>
            <IconButton className="float-right" onClick={onToggleDrawer}>
              <KeyboardDoubleArrowLeftIcon
                data-testid="left-arrow-icon"
                id="left-arrow-icon"
                className={'pinIcon'}
              />
            </IconButton>
          </Box>
          <Tabs
            className="drawer-tab"
            value={activeTab}
            onChange={onTabChange}
            orientation="vertical"
            variant="scrollable"
            scrollButtons={false}
            visibleScrollbar
          >
            {tabs}
          </Tabs>
        </Drawer>
      ) : (
        <div id="tab-drawer-mini" className="tabDrawerMini">
          <IconButton id="tabDrawerToggle" onClick={onToggleDrawer} className="toggleIcon">
            <KeyboardDoubleArrowRightIcon
              data-testid="right-arrow-icon"
              id="right-arrow-icon"
              className={'pinIcon'}
            />
          </IconButton>
          <Tabs
            value={activeTab}
            onChange={onTabChange}
            orientation="vertical"
            variant="scrollable"
            scrollButtons={false}
            visibleScrollbar
            className="tab-toggle drawer-tab"
          >
            {minitabs}
          </Tabs>
        </div>
      )}
    </>
  );
};
